import { getVehicleGeometry } from "./vehicleGeometry.js";
import { PHYSICS_CONFIG } from "./physicsConfig.js";
/** Sweep the pose, resolve to the last safe position AND heading, then separate. */
export function resolveVehicleCollision(previous, proposed, config, sampler, tick) {
    const headingDelta = normalizeAngle(proposed.headingRad - previous.headingRad);
    const travel = Math.hypot(proposed.position.x - previous.position.x, proposed.position.y - previous.position.y) +
        (Math.abs(headingDelta) *
            Math.hypot(config.bodyLength, config.trackWidth)) /
            2;
    const sweepSteps = Math.min(64, Math.max(1, Math.ceil(travel)));
    const at = (fraction) => ({
        ...proposed,
        position: {
            x: previous.position.x +
                (proposed.position.x - previous.position.x) * fraction,
            y: previous.position.y +
                (proposed.position.y - previous.position.y) * fraction,
        },
        headingRad: normalizeAngle(previous.headingRad + headingDelta * fraction),
    });
    let safeFraction = 0;
    let hitFraction = 0;
    let hitPoints = bodyWallContacts(previous, config, sampler);
    if (hitPoints.length === 0) {
        for (let step = 1; step <= sweepSteps; step += 1) {
            hitFraction = step / sweepSteps;
            hitPoints = bodyWallContacts(at(hitFraction), config, sampler);
            if (hitPoints.length > 0)
                break;
            safeFraction = hitFraction;
        }
        if (hitPoints.length === 0)
            return { state: cloneState(proposed) };
        for (let iteration = 0; iteration < 10; iteration += 1) {
            const mid = (safeFraction + hitFraction) / 2;
            if (bodyWallContacts(at(mid), config, sampler).length === 0)
                safeFraction = mid;
            else
                hitFraction = mid;
        }
    }
    let candidate = at(safeFraction);
    const normal = surfaceNormal(hitPoints, sampler, collisionNormal(at(hitFraction).position, hitPoints));
    // Bounded recovery for a pose which began inside a wall (e.g. rotation contact).
    for (let iteration = 0; iteration < 32 && bodyWallContacts(candidate, config, sampler).length > 0; iteration += 1) {
        candidate.position.x += normal.x;
        candidate.position.y += normal.y;
    }
    const separation = bounded(config.wallSeparationDistance, PHYSICS_CONFIG.wallSeparationDistance, 0, 3);
    const separated = {
        ...candidate,
        position: {
            x: candidate.position.x + normal.x * separation,
            y: candidate.position.y + normal.y * separation,
        },
    };
    if (bodyWallContacts(separated, config, sampler).length === 0)
        candidate = separated;
    const basis = getVehicleBasis(candidate.headingRad);
    // The swept pose may rotate back; keep the proposed WORLD momentum first.
    const proposedBasis = getVehicleBasis(proposed.headingRad);
    let vx = proposedBasis.forwardX * proposed.longitudinalSpeed +
        proposedBasis.rightX * proposed.lateralSpeed;
    let vy = proposedBasis.forwardY * proposed.longitudinalSpeed +
        proposedBasis.rightY * proposed.lateralSpeed;
    const inward = vx * normal.x + vy * normal.y;
    if (inward < 0) {
        const restitution = bounded(config.wallRestitution, PHYSICS_CONFIG.wallRestitution, 0, 1);
        const maximum = bounded(config.wallMaximumReboundSpeed, PHYSICS_CONFIG.wallMaximumReboundSpeed, 0, 2);
        const minimum = bounded(config.wallMinimumReboundSpeed, PHYSICS_CONFIG.wallMinimumReboundSpeed, 0, maximum);
        const rebound = Math.min(maximum, Math.max(minimum, -inward * restitution));
        vx += normal.x * (rebound - inward);
        vy += normal.y * (rebound - inward);
    }
    candidate = {
        ...candidate,
        longitudinalSpeed: vx * basis.forwardX + vy * basis.forwardY,
        lateralSpeed: vx * basis.rightX + vy * basis.rightY,
        yawRate: proposed.yawRate *
            bounded(config.wallYawRetention, PHYSICS_CONFIG.wallYawRetention, 0, 1),
    };
    return {
        state: candidate,
        wallHit: telemetry(tick, hitPoints.length, normal, proposed, candidate),
    };
}
function bodyWallContacts(state, config, sampler) {
    const geometry = getVehicleGeometry(state, config);
    const basis = getVehicleBasis(state.headingRad);
    return [
        ...geometry.corners,
        ...geometry.sideMidpoints,
        {
            x: state.position.x + (basis.forwardX * config.bodyLength) / 2,
            y: state.position.y + (basis.forwardY * config.bodyLength) / 2,
        },
        {
            x: state.position.x - (basis.forwardX * config.bodyLength) / 2,
            y: state.position.y - (basis.forwardY * config.bodyLength) / 2,
        },
    ].filter((point) => sampler.sample(point.x, point.y).terrain === "wall");
}
/** Local map gradients give the surface normal, including oblique impacts. */
function surfaceNormal(points, sampler, fallback) {
    let x = 0;
    let y = 0;
    for (const point of points) {
        for (const scale of [1, 2, 3]) {
            let gradientX = 0;
            let gradientY = 0;
            // A disk stencil retains shallow/diagonal surface directions; four
            // binary axis samples would incorrectly quantize them to 45 degrees.
            for (let dx = -4; dx <= 4; dx += 1) {
                for (let dy = -4; dy <= 4; dy += 1) {
                    if (dx * dx + dy * dy > 16)
                        continue;
                    if (sampler.sample(point.x + dx * scale, point.y + dy * scale)
                        .terrain !== "wall") {
                        gradientX += dx;
                        gradientY += dy;
                    }
                }
            }
            const length = Math.hypot(gradientX, gradientY);
            if (length > 0) {
                x += gradientX / length;
                y += gradientY / length;
                break;
            }
        }
    }
    const magnitude = Math.hypot(x, y);
    return magnitude > 0 ? { x: x / magnitude, y: y / magnitude } : fallback;
}
function bounded(value, fallback, minimum, maximum) {
    return Math.max(minimum, Math.min(maximum, value !== undefined && Number.isFinite(value) ? value : fallback));
}
function collisionNormal(center, points) {
    let x = 0;
    let y = 0;
    for (const point of points) {
        const dx = center.x - point.x;
        const dy = center.y - point.y;
        const magnitude = Math.hypot(dx, dy) || 1;
        x += dx / magnitude;
        y += dy / magnitude;
    }
    const magnitude = Math.hypot(x, y) || 1;
    return { x: x / magnitude, y: y / magnitude };
}
function telemetry(tick, contacts, normal, before, after) {
    const velocityImpulse = Math.hypot(before.longitudinalSpeed - after.longitudinalSpeed, before.lateralSpeed - after.lateralSpeed);
    const positionCorrection = Math.hypot(before.position.x - after.position.x, before.position.y - after.position.y);
    return {
        tick,
        contacts,
        impulse: Math.max(0.0001, velocityImpulse + positionCorrection + contacts * 0.01),
        normal,
    };
}
function cloneState(state) {
    return { ...state, position: { ...state.position } };
}
function getVehicleBasis(headingRad) {
    return {
        forwardX: Math.sin(headingRad),
        forwardY: -Math.cos(headingRad),
        rightX: Math.cos(headingRad),
        rightY: Math.sin(headingRad),
    };
}
function normalizeAngle(angle) {
    let normalized = angle;
    while (normalized > Math.PI)
        normalized -= Math.PI * 2;
    while (normalized < -Math.PI)
        normalized += Math.PI * 2;
    return normalized;
}
export function wallContactPoints(state, config) {
    return getVehicleGeometry(state, config).wheels;
}
